import { getActiveRole } from '@fs/zion-admin-mode'
import { Row, Alert, Separator } from '@fs/zion-ui'
import React, { useContext, useEffect, useReducer } from 'react'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { usePageTitle } from '../../app/PageTitleContext'
import { RawData } from '../../common/RawData'
import UserContributionsPersonFilters from './UserContributionsPersonFilters'
import UserContributionsTable from './UserContributionsTable'
import useUserContributions, { actions } from './useUserContributions'
import UserDestructionReportButtonAndDialog from './UserDestructionReportButtonAndDialog'
import UserRollbackSummaryButtonAndDialog from './UserRollbackSummaryButtonAndDialog'
import { PermissionsContext } from '../../app/PermissionsContext'
import UserRollbackResultsButtonAndDialog from './UserRollbackResultsButtonAndDialog'
import UserRollbackButtonAndDialog from './UserRollbackButtonAndDialog'
import UserContributionsTypeFilters from './UserContributionsTypeFilters'
import getTypeFiltersInitialState from '../../common/TypeFiltersState'

export default function UserContributions() {
  usePageView({ page_detail: 'DMConsole:UserContributions' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.user.contributions'))
  const { isRole } = useContext(PermissionsContext)
  const { pageState, pageDispatch, getContributions, rerenderInfiniteScroll, setRerenderInfiniteScroll } =
    useUserContributions()
  const [typeFiltersState, setTypeFiltersState] = useReducer(
    (oldstate, newState) => JSON.parse(JSON.stringify(newState)),
    getTypeFiltersInitialState({ t, type: 'user' })
  )

  useEffect(() => {
    const getActiveRoleAsync = async () => {
      const activeRole = await getActiveRole()
      const action = {
        type: actions.SET_ACTIVE_ROLE,
        payload: activeRole,
      }
      pageDispatch(action)
    }
    if (pageState?.currentUser?.contributorId == null) {
      return
    }
    getContributions(pageState.currentUser.contributorId, null, null, null, null, actions.SET_CONTRIBUTIONS)
    getActiveRoleAsync()
  }, [getContributions, pageDispatch, pageState.currentUser.contributorId])

  return (
    <>
      <Alert type="warning" dismissable>
        {t(
          'user.contributions.filterMessage1',
          'Please be aware that filtering only works on top-level changes. For example, filtering for restored persons will appear in the results, but any sub-level changes caused by this, such as relationships restored, will not appear if filtering for restored relationships. The following types of changes may have sub-level changes:'
        )}
        <br />
        <br />
        {t('user.contributions.filterMessage2', 'Create Person: birth, name, gender, death, etc.')}
        <br />
        {t(
          'user.contributions.filterMessage3',
          'Merge: fact, name, gender, changes, relationship changes, source changes, ordinance changes, etc.'
        )}
        <br />
        {t('user.contributions.filterMessage4', 'Tombstone a person: relationship deletes or remove parent removals')}
        <br />
        {t('user.contributions.filterMessage5', 'Restore a person: relationship restores, or parent adds')}
        <br />
        {t('user.contributions.filterMessage6', 'Unmerge a person: undo all merge changes')}
      </Alert>
      <Separator />
      <Row wrap gutters="xxs">
        <UserContributionsTypeFilters
          growX
          userId={pageState.currentUser.contributorId}
          getContributions={getContributions}
          typeFiltersState={typeFiltersState}
          setTypeFiltersState={setTypeFiltersState}
        />
        <UserContributionsPersonFilters
          pageState={pageState}
          getContributions={getContributions}
          setTypeFiltersState={setTypeFiltersState}
        />
        <span growX />
        {isRole(['Rollback Admin']) && <UserRollbackButtonAndDialog />}
        {isRole(['Rollback Admin']) && <UserRollbackResultsButtonAndDialog />}
        {isRole(['Rollback Admin']) && <UserRollbackSummaryButtonAndDialog />}
        {isRole(['Data Admin 6', 'Engineer Admin', 'Rollback Admin']) && <UserDestructionReportButtonAndDialog />}
      </Row>
      <Row>
        <RawData state={pageState} />
      </Row>
      <UserContributionsTable
        pageState={pageState}
        getContributions={getContributions}
        rerenderInfiniteScroll={rerenderInfiniteScroll}
        setRerenderInfiniteScroll={setRerenderInfiniteScroll}
      />
    </>
  )
}
